import { useEffect } from 'react';
import {
	LOOM_MEETING_NUDGE_SIDENAV_SHOWN,
	LOOM_MEETING_NUDGE_TITLE_SHOWN,
} from '@post-office/browser--growth-starburst/twc-loom-meeting-nudge-sidenav/reminder-event';

import { useTrigger } from '@atlassian/post-office-context/post-office-trigger';

import { fg } from '@confluence/feature-gating';
import { LOOM_NUDGE_LAST_SHOWN_AT } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export const LOOM_NUDGE_FATIGUE_PERIOD_MS: number = 24 * 60 * 60 * 1000;

// This hook has several mount points. Persist the shared shown event only once per page load.
let hasPersistedShownEventOnThisPage = false;

const parseLastShownAt = (state: unknown): number | undefined => {
	if (!state || typeof state !== 'object' || !('lastShownAt' in state)) {
		return undefined;
	}
	const lastShownAt = Number(state.lastShownAt);
	return Number.isFinite(lastShownAt) && lastShownAt >= 0 ? lastShownAt : undefined;
};

const wasShownInTheLastDay = (lastShownAt: number | undefined): boolean =>
	Boolean(lastShownAt && lastShownAt > Date.now() - LOOM_NUDGE_FATIGUE_PERIOD_MS);

export type UseLoomNudgeDailyLimitResult = {
	canShowLoomNudge: boolean;
	loading: boolean;
	error: ReturnType<typeof useGetOnboardingState>['error'];
};

/** Returns the current tenant's Loom nudge fatigue state and any loading error. */
export const useLoomNudgeDailyLimit = (): UseLoomNudgeDailyLimitResult => {
	const { cloudId } = useSessionData();
	const isEnabled =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('twcg-2202-loom-meeting-nudge-ht-gate') || fg('twcg-2202-loom-meeting-nudge-lt-gate');
	const { data, loading, error } = useGetOnboardingState(LOOM_NUDGE_LAST_SHOWN_AT, !isEnabled);
	const { setOnboardingState } = useSetOnboardingState();
	const persistedLastShownAt = parseLastShownAt(deserializeState(data));
	const { isActive: hasTitleNudgeShown } = useTrigger(LOOM_MEETING_NUDGE_TITLE_SHOWN);
	const { isActive: hasSidenavNudgeShown } = useTrigger(LOOM_MEETING_NUDGE_SIDENAV_SHOWN);
	const hasNudgeShownOnThisPage = hasTitleNudgeShown || hasSidenavNudgeShown;

	useEffect(() => {
		if (!isEnabled || !cloudId || !hasNudgeShownOnThisPage || hasPersistedShownEventOnThisPage) {
			return;
		}

		hasPersistedShownEventOnThisPage = true;
		setOnboardingState({ key: LOOM_NUDGE_LAST_SHOWN_AT, value: String(Date.now()) });
	}, [cloudId, hasNudgeShownOnThisPage, isEnabled, setOnboardingState]);

	if (!isEnabled) {
		return { canShowLoomNudge: false, loading: false, error };
	}

	return {
		canShowLoomNudge: Boolean(
			cloudId &&
			!loading &&
			!hasNudgeShownOnThisPage &&
			!hasPersistedShownEventOnThisPage &&
			!wasShownInTheLastDay(persistedLastShownAt),
		),
		loading,
		error,
	};
};

export const clearLoomNudgeDailyLimitForTesting = (): void => {
	hasPersistedShownEventOnThisPage = false;
};
